import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { ActivityProvider } from "@/hooks/useActivityContext";
import { HelmetProvider } from '@dr.pogodin/react-helmet';
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import StudentDashboard from "./pages/StudentDashboard";
import StudentInvitations from "./pages/StudentInvitations";
import StudentCalendar from "./pages/StudentCalendar";
import Auth from "./pages/Auth";
import TestPage from "./pages/TestPage";
import ResultsPage from "./pages/ResultsPage";
import Analysis from "./pages/Analysis";
import AdminQuestions from "./pages/AdminQuestions";
import AdminDashboard from "./pages/AdminDashboard";
import AdminQuestionGenerator from "./pages/AdminQuestionGenerator";
import AdminSubjects from "./pages/AdminSubjects";
import AdminReports from "./pages/AdminReports";
import AdminAILogs from "./pages/AdminAILogs";
import AdminEmailTemplates from "./pages/AdminEmailTemplates";
import AdminEmailCampaigns from "./pages/AdminEmailCampaigns";
import AdminCategorySuggestions from "./pages/AdminCategorySuggestions";
import AdminNotifications from "./pages/AdminNotifications";
import InstructorDashboard from "./pages/InstructorDashboard";
import InstructorTestCreate from "./pages/InstructorTestCreate";
import InstructorTestEdit from "./pages/InstructorTestEdit";
import InstructorTests from "./pages/InstructorTests";
import InstructorStudentLists from "./pages/InstructorStudentLists";
import InstructorCreateStudentList from "./pages/InstructorCreateStudentList";
import InstructorEditStudentList from "./pages/InstructorEditStudentList";
import InstructorTutoringSchedules from "./pages/InstructorTutoringSchedules";
import InstructorCreateTutoringSchedule from "./pages/InstructorCreateTutoringSchedule";
import InstructorSessions from "./pages/InstructorSessions";
import InstructorCalendar from "./pages/InstructorCalendar";
import InstructorAnalytics from "./pages/InstructorAnalytics";
import StudentTutoringSchedules from "./pages/StudentTutoringSchedules";
import ParentAuth from "./pages/ParentAuth";
import ParentDashboard from "./pages/ParentDashboard";
import ParentChildren from "./pages/ParentChildren";
import ParentCalendar from "./pages/ParentCalendar";
import ParentTests from "./pages/ParentTests";
import ParentAnalysis from "./pages/ParentAnalysis";
import Settings from "./pages/Settings";
import Games from "./pages/Games";
import SharedGamePage from "./pages/SharedGamePage";
import Worksheets from "./pages/Worksheets";
import GradeSelection from "@/components/home/GradeSelection";
import InvitedTestPage from "./pages/InvitedTestPage";
import WritingPad from "./pages/WritingPad";
import SubjectSelection from "./pages/SubjectSelection";
import OrgPracticeSettings from "./pages/OrgPracticeSettings";
import OrgsList from "./pages/OrgsList";
import OrgDetail from "./pages/OrgDetail";
import OrgInvitationAccept from "./pages/OrgInvitationAccept";
import OrgCourses from "./pages/OrgCourses";
import OrgCourseEdit from "./pages/OrgCourseEdit";
import OrgLessonEdit from "./pages/OrgLessonEdit";
import StudentLessonView from "./pages/StudentLessonView";
import { StudentCourses, StudentCourseView } from "./pages/StudentCourses";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import PublicShellRoute from "./components/auth/PublicShellRoute";
import RoleProtectedRoute from "./components/auth/RoleProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import AppShell from "./components/layout/AppShell";
import { FloatingChatbot } from "@/components/chatbot/FloatingChatbot";
import RouteAnalytics from "@/components/analytics/RouteAnalytics";
import CookieConsentBanner from "@/components/consent/CookieConsentBanner";
import "./App.css";

const queryClient = new QueryClient();

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <AuthProvider>
              <SettingsProvider>
                <Toaster />
                <BrowserRouter>
                  <RouteAnalytics />
                  <CookieConsentBanner />
                  <ActivityProvider>
                    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
                      {/* Inner ErrorBoundary scopes route render failures so
                          they don't blank the entire shell (providers,
                          toasters, chatbot, etc. stay alive). */}
                      <ErrorBoundary>
                      <Routes>
                        <Route path="/" element={<Index />} />
                        <Route path="/auth" element={<Auth />} />
                        <Route
                          path="/dashboard"
                          element={
                            <ProtectedRoute>
                              <Dashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/student/dashboard"
                          element={
                            <ProtectedRoute>
                              <StudentDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/grade"
                          element={
                            <PublicShellRoute>
                              <GradeSelection />
                            </PublicShellRoute>
                          }
                        />
                        <Route
                          path="/grade/:grade"
                          element={
                            <PublicShellRoute>
                              <SubjectSelection />
                            </PublicShellRoute>
                          }
                        />
                        <Route
                          path="/grade/:grade/subject/:subject"
                          element={
                            <PublicShellRoute>
                              <TestPage />
                            </PublicShellRoute>
                          }
                        />
                        <Route
                          path="/test/:grade/:subject/:category?"
                          element={
                            <PublicShellRoute>
                              <TestPage />
                            </PublicShellRoute>
                          }
                        />
                        <Route
                          path="/results/:sessionId"
                          element={
                            <PublicShellRoute>
                              <ResultsPage />
                            </PublicShellRoute>
                          }
                        />
                        <Route
                          path="/analysis/:grade?/:subject?/:category?"
                          element={
                            <ProtectedRoute>
                              <Analysis />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/games/shared/:token"
                          element={
                            <ProtectedRoute>
                              <SharedGamePage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/games/:grade?/:subject?"
                          element={
                            <PublicShellRoute>
                              <Games />
                            </PublicShellRoute>
                          }
                        />
                        <Route
                          path="/worksheets/:grade?/:subject?"
                          element={
                            <PublicShellRoute>
                              <Worksheets />
                            </PublicShellRoute>
                          }
                        />
                        <Route
                          path="/writing/:grade?"
                          element={
                            <PublicShellRoute>
                              <WritingPad />
                            </PublicShellRoute>
                          }
                        />
                        <Route
                          path="/settings"
                          element={
                            <ProtectedRoute>
                              <Settings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/dashboard"
                          element={
                            <RoleProtectedRoute requiredRole="admin">
                              <AdminDashboard />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/questions"
                          element={
                            <RoleProtectedRoute requiredRole="admin">
                              <AdminQuestions />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/generator"
                          element={
                            <RoleProtectedRoute requiredRole="admin">
                              <AdminQuestionGenerator />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/subjects"
                          element={
                            <RoleProtectedRoute requiredRole="admin">
                              <AdminSubjects />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/reports"
                          element={
                            <RoleProtectedRoute requiredRole="admin">
                              <AdminReports />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/logs"
                          element={
                            <RoleProtectedRoute requiredRole="admin">
                              <AdminAILogs />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/email-templates"
                          element={
                            <RoleProtectedRoute requiredRole="admin">
                              <AdminEmailTemplates />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/email-campaigns"
                          element={
                            <RoleProtectedRoute requiredRole="admin">
                              <AdminEmailCampaigns />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/category-suggestions"
                          element={
                            <RoleProtectedRoute requiredRole="admin">
                              <AdminCategorySuggestions />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/notifications"
                          element={
                            <RoleProtectedRoute requiredRole="admin">
                              <AdminNotifications />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/instructor/dashboard"
                          element={
                            <RoleProtectedRoute requiredRole="instructor">
                              <InstructorDashboard />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/instructor/tests/create"
                          element={
                            <RoleProtectedRoute requiredRole="instructor">
                              <InstructorTestCreate />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/instructor/tests"
                          element={
                            <RoleProtectedRoute requiredRole="instructor">
                              <InstructorTests />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/instructor/tests/:testId/edit"
                          element={
                            <RoleProtectedRoute requiredRole="instructor">
                              <InstructorTestEdit />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/instructor/students"
                          element={
                            <RoleProtectedRoute requiredRole="instructor">
                              <InstructorStudentLists />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/instructor/students/create-list"
                          element={
                            <RoleProtectedRoute requiredRole="instructor">
                              <InstructorCreateStudentList />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/instructor/students/edit-list/:listId"
                          element={
                            <RoleProtectedRoute requiredRole="instructor">
                              <InstructorEditStudentList />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/instructor/tutoring"
                          element={
                            <RoleProtectedRoute requiredRole="instructor">
                              <InstructorTutoringSchedules />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/instructor/tutoring/create"
                          element={
                            <RoleProtectedRoute requiredRole="instructor">
                              <InstructorCreateTutoringSchedule />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/instructor/tutoring/edit/:scheduleId"
                          element={
                            <RoleProtectedRoute requiredRole="instructor">
                              <InstructorCreateTutoringSchedule />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/instructor/sessions"
                          element={
                            <RoleProtectedRoute requiredRole="instructor">
                              <InstructorSessions />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/instructor/calendar"
                          element={
                            <RoleProtectedRoute requiredRole="instructor">
                              <InstructorCalendar />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/instructor/analytics"
                          element={
                            <RoleProtectedRoute requiredRole="instructor">
                              <InstructorAnalytics />
                            </RoleProtectedRoute>
                          }
                        />
                        <Route
                          path="/student/tutoring"
                          element={
                            <ProtectedRoute>
                              <StudentTutoringSchedules />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/student/invitations"
                          element={
                            <ProtectedRoute>
                              <StudentInvitations />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/student/calendar"
                          element={
                            <ProtectedRoute>
                              <StudentCalendar />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="/parent/auth" element={<ParentAuth />} />
                        <Route
                          path="/parent/dashboard"
                          element={
                            <ProtectedRoute>
                              <ParentDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/parent/children"
                          element={
                            <ProtectedRoute>
                              <ParentChildren />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/parent/calendar"
                          element={
                            <ProtectedRoute>
                              <ParentCalendar />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/parent/tests"
                          element={
                            <ProtectedRoute>
                              <ParentTests />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/parent/analysis"
                          element={
                            <ProtectedRoute>
                              <ParentAnalysis />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/test/invitation"
                          element={
                            <ProtectedRoute bare>
                              <InvitedTestPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/orgs"
                          element={
                            <ProtectedRoute>
                              <OrgsList />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/orgs/invitations/accept"
                          element={
                            <ProtectedRoute>
                              <OrgInvitationAccept />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/orgs/:orgId"
                          element={
                            <ProtectedRoute>
                              <OrgDetail />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/org/settings/practice-items"
                          element={
                            <ProtectedRoute>
                              <OrgPracticeSettings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/org/courses"
                          element={
                            <ProtectedRoute>
                              <OrgCourses />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/org/courses/:courseId"
                          element={
                            <ProtectedRoute>
                              <OrgCourseEdit />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/org/lessons/:lessonId"
                          element={
                            <ProtectedRoute>
                              <OrgLessonEdit />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/student/courses"
                          element={
                            <ProtectedRoute>
                              <StudentCourses />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/student/courses/:courseId"
                          element={
                            <ProtectedRoute>
                              <StudentCourseView />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/student/lessons/:lessonId"
                          element={
                            <ProtectedRoute>
                              <StudentLessonView />
                            </ProtectedRoute>
                          }
                        />
                       </Routes>
                      </ErrorBoundary>
                      
                      {/* Global Floating Chatbot */}
                      <FloatingChatbot />
                    </div>
                  </ActivityProvider>
                </BrowserRouter>
              </SettingsProvider>
            </AuthProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;